import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './theme/theme';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AuthCallback from './components/AuthCallback.jsx';
import ForgotPassword from './components/ForgotPassword.jsx';
import UpdatePassword from './components/UpdatePassword.jsx';
import PluginConnect from './components/PluginConnect.jsx';
import Tutorial from './components/Tutorial.jsx';
import { QUICK_START_PATH, PUBLIC_TUTORIALS_PATH } from './utils/routes';
import { Navigate } from 'react-router-dom';

const queryClient = new QueryClient();

function AppContent() {
  const { user, userData, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Routes>
      {/* OAuth callback route - always accessible during auth flow */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/login" element={user && user.email_confirmed_at ? <Navigate to="/overview" replace /> : <Login />} />

      {/* Plugin connect route - outside auth guard; popup opened by WP onboarding wizard */}
      <Route path="/connect" element={<PluginConnect />} />

      {/* Public tutorials — deliberately OUTSIDE the auth guard so it can be
          shared/linked without a login (the Overview "Open tutorials" button
          points here). Same component as the in-app Quick Start Guide tab, so
          the two can never say different things. */}
      <Route path={PUBLIC_TUTORIALS_PATH} element={<Tutorial standalone />} />

      {/* Renamed: /tutorial → /quick-start-guide. Redirect keeps old links,
          bookmarks and anything the plugin may have shipped working. */}
      <Route path="/tutorial" element={<Navigate to={QUICK_START_PATH} replace />} />

      {/* Index route normalizes to the Overview tab */}
      <Route path="/" element={<Navigate to="/overview" replace />} />
      
      {/* Protected routes - redirect to login if not authenticated */}
      <Route
        path="/*"
        element={
          !user || !user.email_confirmed_at ? (
            <Login />
          ) : !userData ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100vh' 
            }}>
              Loading user data...
            </div>
          ) : (
            <Dashboard />
          )
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
